import { throttle } from "lodash-es";
import React, { useCallback, useState } from "react";
import { connect as reactReduxConnect, useDispatch, useSelector, useStore } from "react-redux";
// @ts-expect-error
import VideoLayout from "../../../../../modules/UI/videolayout/VideoLayout";
import { getConferenceNameForTitle } from "../../../base/conference/functions";
import { hangup } from "../../../base/connection/actions.web";
import { isMobileBrowser } from "../../../base/environment/utils";
import { translate } from "../../../base/i18n/functions";
import { setColorAlpha } from "../../../base/util/helpers";
import { openChat, setFocusedTab } from "../../../chat/actions.web";
import Chat from "../../../chat/components/web/Chat";
import { ChatTabs } from "../../../chat/constants";
import { isFileUploadingEnabled, processFiles } from "../../../file-sharing/functions.any";
import MainFilmstrip from "../../../filmstrip/components/web/MainFilmstrip";
import ScreenshareFilmstrip from "../../../filmstrip/components/web/ScreenshareFilmstrip";
import StageFilmstrip from "../../../filmstrip/components/web/StageFilmstrip";
import CalleeInfoContainer from "../../../invite/components/callee-info/CalleeInfoContainer";
import LargeVideo from "../../../large-video/components/LargeVideo.web";
import KnockingParticipantPopup from "../../../lobby/components/web/KnockingParticipantPopup";
import LobbyScreen from "../../../lobby/components/web/LobbyScreen";
import { getIsLobbyVisible } from "../../../lobby/functions";
import { getOverlayToRender } from "../../../overlay/functions.web";
import ParticipantsPane from "../../../participants-pane/components/web/ParticipantsPane";
import Prejoin from "../../../prejoin/components/web/Prejoin";
import { isPrejoinPageVisible } from "../../../prejoin/functions.web";
import ReactionAnimations from "../../../reactions/components/web/ReactionsAnimations";
import ShareMeetingPopup from "../../../share-room/components/web/ShareMeetingPopup";
import { toggleToolboxVisible } from "../../../toolbox/actions.any";
import { fullScreenChanged, hideToolbox, showToolbox } from "../../../toolbox/actions.web";
import JitsiPortal from "../../../toolbox/components/web/JitsiPortal";
import Toolbox from "../../../toolbox/components/web/Toolbox";
import { LAYOUT_CLASSNAMES } from "../../../video-layout/constants";
import { getCurrentLayout } from "../../../video-layout/functions.any";
import VisitorsQueue from "../../../visitors/components/web/VisitorsQueue";
import { showVisitorsQueue } from "../../../visitors/functions";
import { init } from "../../actions.web";
import { maybeShowSuboptimalExperienceNotification } from "../../functions.web";
import { AbstractConference, abstractMapStateToProps } from "../AbstractConference";
import ConferenceInfo from "./ConferenceInfo";
import { default as Notice } from "./Notice";
/**
 * DOM events for when full screen mode has changed. Different browsers need
 * different vendor prefixes.
 *
 * @private
 * @type {Array<string>}
 */
const FULL_SCREEN_EVENTS = ["webkitfullscreenchange", "mozfullscreenchange", "fullscreenchange"];
/**
 * Returns true if the prejoin screen should be displayed and false otherwise.
 *
 * @param {IProps} props - The props object.
 * @returns {boolean} - True if the prejoin screen should be displayed and false otherwise.
 */
function shouldShowPrejoin({ _showLobby, _showPrejoin, _showVisitorsQueue }) {
    return _showPrejoin && !_showVisitorsQueue && !_showLobby;
}
/**
 * The conference page of the Web application.
 */
class Conference extends AbstractConference {
    _originalOnMouseMove;
    _originalOnShowToolbar;
    /**
     * Initializes a new Conference instance.
     *
     * @param {Object} props - The read-only properties with which the new
     * instance is to be initialized.
     */
    constructor(props) {
        super(props);
        const { _mouseMoveCallbackInterval } = props;
        // Throttle and bind this component's mousemove handler to prevent it
        // from firing too often.
        this._originalOnShowToolbar = this._onShowToolbar;
        this._originalOnMouseMove = this._onMouseMove;
        this._onShowToolbar = throttle(() => this._originalOnShowToolbar(), 100, {
            leading: true,
            trailing: false,
        });
        this._onMouseMove = throttle((event) => this._originalOnMouseMove(event), _mouseMoveCallbackInterval, {
            leading: true,
            trailing: false,
        });
        // Bind event handler so it is only bound once for every instance.
        this._onFullScreenChange = this._onFullScreenChange.bind(this);
        this._onVideospaceTouchStart = this._onVideospaceTouchStart.bind(this);
        this._onHideToolbar = this._onHideToolbar.bind(this);
        this._onMouseLeave = this._onMouseLeave.bind(this);
        this._setBackground = this._setBackground.bind(this);
        this._onBeforeUnload = this._onBeforeUnload.bind(this);
        this._onPageHide = this._onPageHide.bind(this);
    }
    /**
     * Start the connection and get the UI ready for the conference.
     *
     * @inheritdoc
     */
    componentDidMount() {
        document.title = "اتاق جلسه | دیدار";
        this._start();
        // Add event listeners for browser/tab close
        window.addEventListener("beforeunload", this._onBeforeUnload);
        window.addEventListener("pagehide", this._onPageHide);
    }
    /**
     * Calls into legacy UI to update the application layout, if necessary.
     *
     * @inheritdoc
     * returns {void}
     */
    componentDidUpdate(prevProps) {
        if (this.props._shouldDisplayTileView === prevProps._shouldDisplayTileView) {
            return;
        }
        // TODO: For now VideoLayout is being called as LargeVideo and Filmstrip
        // sizing logic is still handled outside of React. Once all components
        // are in react they should calculate size on their own as much as
        // possible and pass down sizings.
        VideoLayout.refreshLayout();
    }
    /**
     * Disconnect from the conference when component will be
     * unmounted.
     *
     * @inheritdoc
     */
    componentWillUnmount() {
        APP.UI.unbindEvents();
        FULL_SCREEN_EVENTS.forEach((name) => document.removeEventListener(name, this._onFullScreenChange));
        // Remove browser close event listeners
        window.removeEventListener("beforeunload", this._onBeforeUnload);
        window.removeEventListener("pagehide", this._onPageHide);
        APP.conference.isJoined() && this.props.dispatch(hangup());
    }
    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        const { _isAnyOverlayVisible, _layoutClassName, _notificationsVisible, _overflowDrawer, _showLobby, _showPrejoin, _showVisitorsQueue, t, } = this.props;
        return (React.createElement("div", { id: "layout_wrapper", onMouseEnter: this._onMouseEnter, onMouseLeave: this._onMouseLeave, onMouseMove: this._onMouseMove, ref: this._setBackground },
            React.createElement(Chat, null),
            React.createElement("div", { className: _layoutClassName, id: "videoconference_page", onMouseMove: isMobileBrowser() ? undefined : this._onShowToolbar },
                _showPrejoin || _showLobby || React.createElement(ConferenceInfo, null),
                React.createElement(Notice, null),
                React.createElement("img", { alt: "Didar", className: "conference-didar-logo", src: "images/didar-logo.png" }),
                React.createElement("div", { id: "videospace", onTouchStart: this._onVideospaceTouchStart },
                    React.createElement(LargeVideo, null),
                    _showPrejoin || _showLobby || (React.createElement(React.Fragment, null,
                        React.createElement(StageFilmstrip, null),
                        React.createElement(ScreenshareFilmstrip, null),
                        React.createElement(MainFilmstrip, null)))),
                _showPrejoin || _showLobby || (React.createElement(React.Fragment, null,
                    React.createElement("span", { "aria-level": 1, className: "sr-only", role: "heading" }, t("toolbar.accessibilityLabel.heading")),
                    React.createElement(Toolbox, null))),
                _notificationsVisible &&
                    !_isAnyOverlayVisible &&
                    (_overflowDrawer ? (React.createElement(JitsiPortal, { className: "notification-portal" }, this.renderNotificationsContainer({ portal: true }))) : (this.renderNotificationsContainer())),
                React.createElement(CalleeInfoContainer, null),
                shouldShowPrejoin(this.props) && React.createElement(Prejoin, null),
                _showLobby && !_showVisitorsQueue && React.createElement(LobbyScreen, null),
                _showVisitorsQueue && React.createElement(VisitorsQueue, null)),
            React.createElement(ParticipantsPane, null),
            React.createElement(ReactionAnimations, null),
            React.createElement(ShareMeetingPopup, null),
            React.createElement(KnockingParticipantPopup, null)));
    }
    /**
     * Sets custom background opacity based on config. It also applies the
     * opacity on parent element, as the parent element is not accessible directly,
     * only though it's child.
     *
     * @param {Object} element - The DOM element for which to apply opacity.
     *
     * @private
     * @returns {void}
     */
    _setBackground(element) {
        if (!element) {
            return;
        }
        if (this.props._backgroundAlpha !== undefined) {
            const elemColor = element.style.background;
            const alphaElemColor = setColorAlpha(elemColor, this.props._backgroundAlpha);
            element.style.background = alphaElemColor;
            if (element.parentElement) {
                const parentColor = element.parentElement.style.background;
                const alphaParentColor = setColorAlpha(parentColor, this.props._backgroundAlpha);
                element.parentElement.style.background = alphaParentColor;
            }
        }
    }
    /**
     * Handler used for touch start on Video container.
     *
     * @private
     * @returns {void}
     */
    _onVideospaceTouchStart() {
        this.props.dispatch(toggleToolboxVisible());
    }
    /**
     * Updates the Redux state when full screen mode has been enabled or
     * disabled.
     *
     * @private
     * @returns {void}
     */
    _onFullScreenChange() {
        this.props.dispatch(fullScreenChanged(APP.UI.isFullScreen()));
    }
    /**
     * Triggers iframe API mouseEnter event.
     *
     * @param {MouseEvent} event - The mouse event.
     * @private
     * @returns {void}
     */
    _onMouseEnter(event) {
        APP.API.notifyMouseEnter(event);
    }
    /**
     * Triggers iframe API mouseLeave event and hides the toolbar.
     *
     * @param {MouseEvent} event - The mouse event.
     * @private
     * @returns {void}
     */
    _onMouseLeave(event) {
        APP.API.notifyMouseLeave(event);
        if (!isMobileBrowser()) {
            this._onHideToolbar();
        }
    }
    /**
     * Triggers iframe API mouseMove event.
     *
     * @param {MouseEvent} event - The mouse event.
     * @private
     * @returns {void}
     */
    _onMouseMove(event) {
        APP.API.notifyMouseMove(event);
    }
    /**
     * Displays the toolbar.
     *
     * @private
     * @returns {void}
     */
    _onShowToolbar() {
        this.props.dispatch(showToolbox());
    }
    /**
     * Hides the toolbar (force hide).
     *
     * @private
     * @returns {void}
     */
    _onHideToolbar() {
        this.props.dispatch(hideToolbox(true));
    }
    /**
     * Attempts a synchronous XMPP disconnect by sending unavailable presence
     * directly through the underlying connection. This ensures the server
     * removes the participant immediately on tab close/reload, preventing
     * ghost participants.
     *
     * @private
     * @returns {void}
     */
    _trySyncXmppDisconnect() {
        try {
            // @ts-ignore - accessing internal XMPP connection for sync disconnect
            const connection = APP.connection?.xmpp?.connection;
            if (connection && connection.connected) {
                // Switch Strophe to synchronous mode for immediate send
                connection.sync = true;
                // Send unavailable presence to signal leaving
                // Use Strophe.Builder if available, otherwise raw send
                if (typeof $pres === 'function') {
                    connection.send($pres({ type: 'unavailable' }));
                }
                // Flush any pending stanzas
                connection.flush();
                // Disconnect the underlying XMPP connection
                connection.disconnect();
            }
        }
        catch (e) {
            // Swallow errors — best effort during unload
        }
    }
    /**
     * Handles browser close/refresh - triggers hangup.
     *
     * @private
     * @returns {void}
     */
    _onBeforeUnload() {
        if (APP.conference.isJoined()) {
            // Use sendBeacon for reliable delivery when page is closing
            const meetingUrl = window.location.href;
            navigator.sendBeacon?.("/api/leave-meeting", JSON.stringify({ url: meetingUrl }));
            // Attempt synchronous XMPP disconnect to remove participant immediately
            this._trySyncXmppDisconnect();
            // Trigger hangup (async, may not complete before page unloads)
            APP.conference.hangup(false);
        }
    }
    /**
     * Handles page hide event (works better on mobile and when browser is force closed).
     *
     * @param {PageTransitionEvent} event - The pagehide event.
     * @private
     * @returns {void}
     */
    _onPageHide(event) {
        // Only hangup if page is not being cached (persisted)
        if (!event.persisted && APP.conference.isJoined()) {
            this._trySyncXmppDisconnect();
            APP.conference.hangup(false);
        }
    }
    /**
     * Until we don't rewrite UI using react components
     * we use UI.start from old app. Also method translates
     * component right after it has been mounted.
     *
     * @inheritdoc
     */
    _start() {
        APP.UI.start();
        APP.UI.bindEvents();
        FULL_SCREEN_EVENTS.forEach((name) => document.addEventListener(name, this._onFullScreenChange));
        const { dispatch, t } = this.props;
        // if we will be showing prejoin we don't want to call connect from init.
        // Connect will be dispatched from prejoin screen.
        dispatch(init(!shouldShowPrejoin(this.props)));
        maybeShowSuboptimalExperienceNotification(dispatch, t);
    }
}
/**
 * Maps (parts of) the Redux state to the associated props for the
 * {@code Conference} component.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {IProps}
 */
function _mapStateToProps(state) {
    const { backgroundAlpha, mouseMoveCallbackInterval } = state["features/base/config"];
    const { overflowDrawer } = state["features/toolbox"];
    return {
        ...abstractMapStateToProps(state),
        _backgroundAlpha: backgroundAlpha,
        _isAnyOverlayVisible: Boolean(getOverlayToRender(state)),
        _layoutClassName: LAYOUT_CLASSNAMES[getCurrentLayout(state) ?? ""],
        _mouseMoveCallbackInterval: mouseMoveCallbackInterval,
        _overflowDrawer: overflowDrawer,
        _roomName: getConferenceNameForTitle(state),
        _showLobby: getIsLobbyVisible(state),
        _showPrejoin: isPrejoinPageVisible(state),
        _showVisitorsQueue: showVisitorsQueue(state),
    };
}
export default reactReduxConnect(_mapStateToProps)(translate((props) => {
    const dispatch = useDispatch();
    const store = useStore();
    const [isDragging, setIsDragging] = useState(false);
    const { isOpen: isChatOpen } = useSelector((state) => state["features/chat"]);
    const isFileUploadEnabled = useSelector(isFileUploadingEnabled);
    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);
    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);
    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isFileUploadEnabled) {
            return;
        }
        if (isDragging) {
            if (!isChatOpen) {
                dispatch(openChat());
            }
            dispatch(setFocusedTab(ChatTabs.FILE_SHARING));
        }
    }, [isChatOpen, isDragging, isFileUploadEnabled]);
    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (!isFileUploadEnabled) {
            return;
        }
        if (e.dataTransfer.files?.length > 0) {
            processFiles(e.dataTransfer.files, store);
        }
    }, [isFileUploadEnabled, processFiles]);
    return (React.createElement("div", { onDragEnter: handleDragEnter, onDragLeave: handleDragLeave, onDragOver: handleDragOver, onDrop: handleDrop },
        React.createElement(Conference, { ...props })));
}));
